import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Loader2, TriangleAlert, X } from "lucide-react";
import PropTypes from "prop-types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import { cn } from "@/lib/utils";
import PageHeader from "../PageHeader";
import AgentLoginModal from "./AgentLoginModal";
import MintAgentModal from "./MintAgentModal";
import useAgents from "../../hooks/useAgents";
import useMe from "../../hooks/useMe";
import useHouseholdUsers from "../../hooks/useHouseholdUsers";
import { agentApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import { displayName, relativeTime } from "../tasks/userDisplay";
import UserAvatar from "../people/UserAvatar";

// Only for the fallback error line, when the server sent no detail.
const ACTION_VERBS = {
  rotate: "rotate the token for",
  "provision-login": "give a login to",
  "reset-login": "reset the login for",
  deactivate: "deactivate",
  reactivate: "reactivate",
};

/**
 * The Agents console: every agent identity, what it has been doing, and
 * the buttons to mint, rotate, retire one, and give it a browser login.
 * Every person mints and manages their OWN agents (household sharing D6);
 * staff manage all of them, and only staff hand an agent to a different
 * person (the owner select). The API enforces the same rules.
 */
function AgentList() {
  const { agents, loading, error, refresh } = useAgents();
  const { me, error: meError, refresh: refreshMe } = useMe();
  const { users, error: usersError } = useHouseholdUsers();
  // Only people can own a library. `kind` is required on this payload, so
  // a row without one is malformed — exclude it rather than fail open.
  const people = users.filter((u) => u.kind === "human");
  // Unknown account → hide the staff controls (the opposite of the task
  // modal's "unknown me shows everything"): the fallback there is a 403 on
  // an edit; here it would be a 403 on minting or killing a credential,
  // and hiding is the safe side. The failure is surfaced below instead.
  const isStaff = Boolean(me?.is_staff);
  // A person manages the agents they own. `kind` must be known to be
  // "human" — an unknown account shows no controls (the fallback would be
  // a 403 on minting or killing a credential).
  const isPerson = me?.kind === "human";
  const canManage = (agent) =>
    isStaff || (isPerson && me?.id != null && agent.owner?.id === me.id);

  const [mintOpen, setMintOpen] = useState(false);
  const [minted, setMinted] = useState(null); // {username, token} after Rotate
  const [loginOpen, setLoginOpen] = useState(false);
  // {username, password, reset} after a login was provisioned or reset.
  // Held apart from `minted` so the two secret screens can never be
  // confused — and each is cleared when the other is set.
  const [credentials, setCredentials] = useState(null);
  const [pending, setPending] = useState(null); // {id, action} awaiting confirm
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);
  // One mutation at a time: two credential responses in flight would let
  // the second secret screen replace the first before it was copied.
  const busyAny = busyId != null;

  // An armed confirm disarms itself after a few seconds; this is a page
  // that stays open, and a stray click on a stale "Confirm deactivate"
  // shouldn't retire an agent. Cleared on every change of `pending`.
  useEffect(() => {
    if (!pending) return undefined;
    const t = setTimeout(() => setPending(null), 5000);
    return () => clearTimeout(t);
  }, [pending]);

  const handleMint = async (data) => {
    const { data: agent } = await agentApi.create(data);
    await refresh({ background: true });
    return agent;
  };

  const closeMint = () => {
    setMintOpen(false);
    setMinted(null);
  };

  const closeLogin = () => {
    setLoginOpen(false);
    setCredentials(null);
  };

  // Staged, not immediate. Every other control on this page with
  // consequences (rotate, deactivate, reset login) confirms first; this one
  // fired on a native <select> change — a keyboard arrow-key committed it —
  // and it is the control with CROSS-HUMAN blast radius: it moves another
  // person's library rows and costs them read and delete (close-out review).
  const [pendingOwner, setPendingOwner] = useState(null);

  const setOwner = async (agent, value) => {
    setBusyId(agent.id);
    setActionError(null);
    try {
      await agentApi.update(agent.id, {
        owner_id: value ? Number(value) : null,
      });
      await refresh({ background: true });
      setPendingOwner(null);
    } catch (err) {
      setActionError(
        apiErrorMessage(err, `Failed to set the owner for ${displayName(agent)}`),
      );
    } finally {
      setBusyId(null);
    }
  };

  const runAction = async (agent, action) => {
    setBusyId(agent.id);
    setActionError(null);
    try {
      if (action === "rotate") {
        const { data } = await agentApi.rotate(agent.id);
        setCredentials(null);
        setMinted({ username: data.username, token: data.token });
        setMintOpen(true);
      } else if (action === "provision-login" || action === "reset-login") {
        const reset = action === "reset-login";
        const { data } = reset
          ? await agentApi.resetLogin(agent.id)
          : await agentApi.provisionLogin(agent.id);
        setMinted(null);
        setCredentials({
          username: data.username,
          password: data.password,
          reset,
        });
        setLoginOpen(true);
      } else if (action === "deactivate") {
        await agentApi.deactivate(agent.id);
      } else if (action === "reactivate") {
        await agentApi.reactivate(agent.id);
      }
      await refresh({ background: true });
    } catch (err) {
      setActionError(
        apiErrorMessage(
          err,
          `Failed to ${ACTION_VERBS[action] || action} ${displayName(agent)}`,
        ),
      );
    } finally {
      setBusyId(null);
      setPending(null);
    }
  };

  // Two-step confirm, like task delete: first click arms, second runs.
  // Whoever was clicked: focus goes back there when the dialog closes.
  const openerRef = useRef(null);

  const confirmOrRun = (agent, action, event) => {
    if (event?.currentTarget) openerRef.current = event.currentTarget;
    if (pending && pending.id === agent.id && pending.action === action) {
      runAction(agent, action);
    } else {
      setPending({ id: agent.id, action });
    }
  };

  const armed = (agent, action) =>
    pending && pending.id === agent.id && pending.action === action;

  return (
    <div className="page-container mt-6 pt-4">
      <span className="sr-only" data-testid="agent-credentials-state">
        {credentials ? "held" : "cleared"}
      </span>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <PageHeader
          title="Agents"
          subtitle="Programs with their own mono account — they work the household's projects and report back in task threads"
        />
        {(isStaff || isPerson) && (
          <Button
            className="h-11 md:h-9"
            onClick={(event) => {
              openerRef.current = event.currentTarget;
              setMintOpen(true);
            }}
            disabled={busyAny}
          >
            New agent
          </Button>
        )}
      </div>

      {usersError && (
        <Alert className="mb-4">
          <AlertDescription className="flex items-center gap-3">
            <span className="flex-1">
              Couldn&apos;t load the list of people, so library owners
              can&apos;t be changed right now.
            </span>
          </AlertDescription>
        </Alert>
      )}

      {meError && (
        <Alert className="mb-4">
          <AlertDescription className="flex items-center gap-3">
            <span className="flex-1">
              Couldn&apos;t confirm your account, so staff controls are hidden.
            </span>
            <Button size="sm" variant="outline" onClick={refreshMe}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {actionError && (
        <Alert variant="destructive" className="mb-4 pr-10">
          <AlertDescription>{actionError}</AlertDescription>
          <DismissButton onClick={() => setActionError(null)} />
        </Alert>
      )}

      {loading && (
        <div className="py-12 text-center" role="status">
          <Loader2 className="inline size-6 animate-spin text-muted-foreground" />
          <span className="sr-only">Loading agents...</span>
        </div>
      )}

      {!loading && error && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center gap-3">
            <span className="flex-1">
              Failed to load agents. Please try again.
            </span>
            <Button
              size="sm"
              variant="outline"
              className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => refresh()}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!loading && !error && agents.length === 0 && (
        <div className="py-12 text-center text-muted-foreground">
          <p className="mb-1">No agents yet.</p>
          <p className="text-sm mb-0">
            An agent is a mono account for a program. It signs in with a token,
            sees the projects you mark <em>household</em>, and can claim tasks
            and comment on them under its own name.
            {isStaff || isPerson
              ? " Mint one to get its token."
              : ""}{" "}
            <a
              href="https://github.com/BrandonHBodine/mono/blob/main/mcp_server/README.md"
              target="_blank"
              rel="noreferrer"
            >
              How the MCP setup works
            </a>
            .
          </p>
        </div>
      )}

      {!loading && !error && agents.length > 0 && (
        <div className="flex flex-col gap-2">
          {agents.map((agent) => {
            const s = agent.stats || {};
            const name = displayName(agent);
            return (
              <div
                key={agent.id}
                className={cn(
                  "rounded-lg border bg-card p-4 text-card-foreground shadow-xs",
                  !agent.is_active && "opacity-75",
                )}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1">
                    <h3 className="mb-1 flex items-center gap-2 font-display text-lg font-semibold">
                      <UserAvatar user={agent} size="md" decorative />
                      <Link to={`/agents/${agent.id}`} className="no-underline">
                        {name}
                      </Link>{" "}
                      <Badge variant="secondary" className="font-normal">
                        agent
                      </Badge>
                      {agent.has_login && (
                        <>
                          {" "}
                          <Badge
                            variant="outline"
                            className="font-normal"
                            title="Has a browser login — it can sign in and drive the app as itself"
                          >
                            login
                          </Badge>
                        </>
                      )}
                      {!agent.is_active && (
                        <>
                          {" "}
                          <Badge variant="outline" className="font-normal">
                            inactive
                          </Badge>
                        </>
                      )}
                    </h3>
                    {agent.description && (
                      <p className="mb-1 text-muted-foreground">
                        {agent.description}
                      </p>
                    )}
                    <div className="text-sm text-muted-foreground">
                      {agent.last_seen_at
                        ? `Last seen ${relativeTime(agent.last_seen_at)}`
                        : "Never seen"}
                      {" · "}
                      {s.assigned_open ?? 0} open assigned ·{" "}
                      {s.claimed_now ?? 0} claimed now · {s.comments_7d ?? 0}{" "}
                      comments this week
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">
                        {agent.owner ? (
                          `Works in ${displayName(agent.owner)}'s library`
                        ) : (
                          // One text node (tests and screen readers read
                          // the sentence whole); the amber mark is
                          // decorative — `text-warning` on the text itself
                          // measured 3.08:1 (#34 review B).
                          <>
                            <TriangleAlert
                              aria-hidden="true"
                              className="mr-1 inline size-4 align-text-bottom text-warning"
                            />
                            No library — assign an owner to let it read one
                          </>
                        )}
                      </span>
                      {isStaff && (
                        <div className="max-w-[190px]">
                          <NativeSelect
                            aria-label={`Library owner for ${name}`}
                            value={
                              pendingOwner?.agentId === agent.id
                                ? pendingOwner.value
                                : (agent.owner?.id ?? "")
                            }
                            disabled={busyAny || Boolean(usersError)}
                            onChange={(e) =>
                              setPendingOwner({
                                agentId: agent.id,
                                value: e.target.value,
                                // The name the warning below shows, not sent.
                                username:
                                  displayName(
                                    people.find(
                                      (p) => String(p.id) === e.target.value,
                                    ),
                                  ) || null,
                              })
                            }
                          >
                            <option value="">No owner</option>
                            {/* The directory lists active accounts only, so a
                              deactivated owner has no option — show it
                              anyway, or the select would read "No owner"
                              next to a line naming one. */}
                            {agent.owner &&
                              !people.some((p) => p.id === agent.owner.id) && (
                                <option value={String(agent.owner.id)} disabled>
                                  {displayName(agent.owner)} (inactive)
                                </option>
                              )}
                            {people.map((p) => (
                              <option key={p.id} value={String(p.id)}>
                                {displayName(p)}
                              </option>
                            ))}
                          </NativeSelect>
                          {/* Say what this does before it is done: owner is the
                            visibility switch for knowledge, so changing it
                            MOVES every document this agent has written into
                            the new owner's library and out of the old one.
                            See "Risks, named" in
                            projects/shared-library/spec.md. */}
                          {pendingOwner?.agentId === agent.id ? (
                            <Alert className="mt-2">
                              <AlertDescription>
                                {pendingOwner.username ? (
                                  <>
                                    Every document and snippet{" "}
                                    <strong>{name}</strong> has saved
                                    moves into{" "}
                                    <strong>{pendingOwner.username}</strong>
                                    &apos;s library, who can then delete them
                                    {agent.owner
                                      ? ` — and ${displayName(agent.owner)} loses access to work they can read and delete right now`
                                      : ""}
                                    . {name} also gains read and write
                                    over everything {pendingOwner.username}{" "}
                                    already owns. Study cards and decks do not
                                    move.
                                  </>
                                ) : (
                                  <>
                                    Everything <strong>{name}</strong>{" "}
                                    has saved becomes invisible to every person
                                    — it stays with the agent, which nobody can
                                    read. Giving it an owner again restores it.
                                  </>
                                )}
                                <div className="mt-2 flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    disabled={busyAny}
                                    onClick={() =>
                                      setOwner(agent, pendingOwner.value)
                                    }
                                  >
                                    Move them
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={busyAny}
                                    onClick={() => setPendingOwner(null)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </AlertDescription>
                            </Alert>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              Changing this moves everything {name}{" "}
                              has saved between libraries — you will be asked to
                              confirm.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {canManage(agent) && (
                    <div className="flex items-center gap-2">
                      {agent.is_active ? (
                        <>
                          <Button
                            size="sm"
                            variant={
                              armed(agent, "rotate") ? "secondary" : "outline"
                            }
                            disabled={busyAny}
                            aria-label={`Rotate token for ${name}`}
                            onClick={(event) =>
                              confirmOrRun(agent, "rotate", event)
                            }
                          >
                            {armed(agent, "rotate")
                              ? "Confirm rotate"
                              : "Rotate token"}
                          </Button>
                          {agent.has_login ? (
                            <Button
                              size="sm"
                              variant={
                                armed(agent, "reset-login")
                                  ? "secondary"
                                  : "outline"
                              }
                              disabled={busyAny}
                              aria-label={`Reset login for ${name}`}
                              title="New password; every session this agent has open stops working. Its API token is untouched."
                              onClick={(event) =>
                                confirmOrRun(agent, "reset-login", event)
                              }
                            >
                              {armed(agent, "reset-login")
                                ? "Confirm reset"
                                : "Reset login"}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant={
                                armed(agent, "provision-login")
                                  ? "secondary"
                                  : "outline"
                              }
                              disabled={busyAny}
                              aria-label={`Give ${name} a login`}
                              title="A username and password so it can sign in and drive the app as itself"
                              onClick={(event) =>
                                confirmOrRun(agent, "provision-login", event)
                              }
                            >
                              {armed(agent, "provision-login")
                                ? "Confirm login"
                                : "Give it a login"}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant={
                              armed(agent, "deactivate")
                                ? "destructive"
                                : "outline"
                            }
                            className={
                              armed(agent, "deactivate")
                                ? undefined
                                : "border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            }
                            disabled={busyAny}
                            aria-label={`Deactivate ${name}`}
                            onClick={(event) =>
                              confirmOrRun(agent, "deactivate", event)
                            }
                          >
                            {armed(agent, "deactivate")
                              ? "Confirm deactivate"
                              : "Deactivate"}
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          variant={
                            armed(agent, "reactivate") ? "secondary" : "outline"
                          }
                          disabled={busyAny}
                          aria-label={`Reactivate ${name}`}
                          title="Restores the previous token — rotate it afterwards if it leaked"
                          onClick={(event) =>
                            confirmOrRun(agent, "reactivate", event)
                          }
                        >
                          {armed(agent, "reactivate")
                            ? "Confirm reactivate"
                            : "Reactivate"}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <MintAgentModal
        show={mintOpen}
        onHide={closeMint}
        onMint={handleMint}
        minted={minted}
        returnFocusTo={openerRef}
      />

      <AgentLoginModal
        show={loginOpen}
        onHide={closeLogin}
        // Belt: forget again after the fade, in case a caller only flips show.
        onExited={() => setCredentials(null)}
        credentials={credentials}
        returnFocusTo={openerRef}
      />
    </div>
  );
}

/** The X on a dismissible alert. */
function DismissButton({ onClick }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="absolute top-1.5 right-1.5 size-7"
      aria-label="Close"
      onClick={onClick}
    >
      <X />
    </Button>
  );
}

DismissButton.propTypes = { onClick: PropTypes.func.isRequired };

export default AgentList;
